/**
 * API Log tab.
 *
 * Controls the historical API-log S3 migration: batch size, the UTC window
 * when a run is allowed, and separate ON/OFF switches for the active and
 * archive tables.
 */

import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { RefreshCw } from 'lucide-react'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { SectionCard } from '@/components/ui/SectionCard'
import {
  buildApiLogPayload,
  getApiLogSummary,
  saveConfig,
  type ApiLogSummary,
  type ApiLogSummaryTarget,
} from '../../services/configService'
import type { ApiLogFormValues, OnOff } from '../../types/config'
import type { ApiLogTabProps } from '../../types'
import {
  FieldLegend,
  integerTextInputAttrs,
  LabeledInput,
  SegmentedButtonGroup,
} from './formPrimitives'

const EMPTY: ApiLogFormValues = {
  API_LOG_BATCH_SIZE: '',
  API_LOGS_MIGRATION_TIME_START_TIME: '',
  API_LOGS_MIGRATION_TIME_END_TIME: '',
  API_LOGS_MIGRATION_ACTIVE: '',
  API_LOGS_MIGRATION_ARCHIVE: '',
}

const CLOCK_TIME = /^([01]\d|2[0-3]):[0-5]\d$/

function validateBatchSize(value: string): true | string {
  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) {
    return 'Batch size must be a whole number of at least 1'
  }
  if (Number(trimmed) < 1) {
    return 'Batch size must be a whole number of at least 1'
  }
  return true
}

function validateClockTime(value: string): true | string {
  if (!CLOCK_TIME.test(value.trim())) {
    return 'Enter a time as HH:MM'
  }
  return true
}

export function ApiLogTab({ initialValues, isLoading, onSaved }: ApiLogTabProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<ApiLogFormValues>({ defaultValues: EMPTY, mode: 'onChange' })

  useEffect(() => {
    if (initialValues) reset(initialValues)
  }, [initialValues, reset])

  const startTime = watch('API_LOGS_MIGRATION_TIME_START_TIME')
  const endTime = watch('API_LOGS_MIGRATION_TIME_END_TIME')
  const sameWindow = startTime.trim() !== '' && startTime.trim() === endTime.trim()

  const onSubmit = async (values: ApiLogFormValues) => {
    if (values.API_LOGS_MIGRATION_TIME_START_TIME.trim() === values.API_LOGS_MIGRATION_TIME_END_TIME.trim()) {
      return
    }
    try {
      setSubmitLoading(true)
      await saveConfig(buildApiLogPayload(values))
      showSuccess('Config Change Successfully!')
      reset(values)
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <div className="space-y-6">
    {isLoading ? (
      <ModuleScreenLoading minHeight="300px" />
    ) : (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <SectionCard number={1} title="S3 Migration">
        <p className="mb-4 max-w-2xl text-xs text-slate-500">
          Times are UTC. The start time is included and the end time is not. An earlier end
          time means the window crosses midnight.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
          <div>
            <LabeledInput
              label="Batch Size"
              requiredMark
              {...integerTextInputAttrs}
              {...register('API_LOG_BATCH_SIZE', {
                required: 'Batch size is required',
                validate: validateBatchSize,
              })}
            />
            {errors.API_LOG_BATCH_SIZE?.message && (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {errors.API_LOG_BATCH_SIZE.message}
              </p>
            )}
          </div>
          <div>
            <LabeledInput
              label="Start Time (UTC)"
              requiredMark
              type="time"
              step={60}
              {...register('API_LOGS_MIGRATION_TIME_START_TIME', {
                required: 'Start time is required',
                validate: validateClockTime,
              })}
            />
            {errors.API_LOGS_MIGRATION_TIME_START_TIME?.message && (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {errors.API_LOGS_MIGRATION_TIME_START_TIME.message}
              </p>
            )}
          </div>
          <div>
            <LabeledInput
              label="End Time (UTC)"
              requiredMark
              type="time"
              step={60}
              {...register('API_LOGS_MIGRATION_TIME_END_TIME', {
                required: 'End time is required',
                validate: validateClockTime,
              })}
            />
            {errors.API_LOGS_MIGRATION_TIME_END_TIME?.message && (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {errors.API_LOGS_MIGRATION_TIME_END_TIME.message}
              </p>
            )}
            {sameWindow && !errors.API_LOGS_MIGRATION_TIME_END_TIME?.message && (
              <p className="mt-1 text-xs text-red-600" role="alert">
                Start and end time must be different
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-8">
          <FieldLegend label="Active Logs">
            <SegmentedButtonGroup
              ariaLabel="Active API log migration"
              value={watch('API_LOGS_MIGRATION_ACTIVE') ?? ''}
              onChange={(v) =>
                setValue('API_LOGS_MIGRATION_ACTIVE', v as OnOff, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              options={[
                { value: 'ON', label: 'ON' },
                { value: 'OFF', label: 'OFF' },
              ]}
            />
          </FieldLegend>
          <FieldLegend label="Archive Logs">
            <SegmentedButtonGroup
              ariaLabel="Archive API log migration"
              value={watch('API_LOGS_MIGRATION_ARCHIVE') ?? ''}
              onChange={(v) =>
                setValue('API_LOGS_MIGRATION_ARCHIVE', v as OnOff, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              options={[
                { value: 'ON', label: 'ON' },
                { value: 'OFF', label: 'OFF' },
              ]}
            />
          </FieldLegend>
        </div>
      </SectionCard>

      <div>
        <button
          type="submit"
          disabled={!isDirty || submitLoading || !isValid || sameWindow}
          className={primaryEntityActionButtonClass}
        >
          {submitLoading ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </form>
    )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ApiLogSummaryCard title="Archive Records" target="archive" />
        <ApiLogSummaryCard title="Active Records" target="active" />
      </div>
    </div>
  )
}

function formatCount(value: number | null): string {
  if (value == null) return '—'
  return value.toLocaleString('en-US')
}

function ApiLogSummaryCard({
  title,
  target,
}: {
  title: string
  target: ApiLogSummaryTarget
}) {
  const [summary, setSummary] = useState<ApiLogSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setSummary(await getApiLogSummary(target))
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : 'Failed to load summary'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [target])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 text-xs text-slate-500">Migrated Records / Total Records</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className={cn(secondaryEntityActionButtonClass, 'h-9 min-w-0 px-3')}
          aria-label={`Refresh ${title}`}
        >
          <RefreshCw className={cn('mr-1.5 h-4 w-4', loading && 'animate-spin')} aria-hidden />
          Refresh
        </button>
      </div>
      <p className="mt-4 text-2xl font-semibold tracking-tight text-slate-900">
        {loading && !summary ? 'Loading…' : `${formatCount(summary?.migrated ?? null)} / ${formatCount(summary?.total ?? null)}`}
      </p>
      {error && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  )
}
